/**
 * SpecialtyContext
 * ──────────────────────────────────────────────────────────
 * Provides the currently-selected specialty across Lumina.
 * Backed by localStorage for persistence. The demo switcher
 * in the Clinical Focus top bar mutates this state so the
 * whole app (vocab, CPTs, panels) updates instantly.
 */
import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  SPECIALTIES, getSpecialty, loadSpecialtyId, saveSpecialtyId,
} from '../config/specialtyConfig';

const SpecialtyContext = createContext({
  specialtyId: 'internal_medicine',
  specialty: getSpecialty('internal_medicine'),
  setSpecialtyId: () => {},
  specialties: SPECIALTIES,
});

export const SpecialtyProvider = ({ children }) => {
  const [specialtyId, setSpecialtyIdState] = useState(() => loadSpecialtyId());
  const specialty = getSpecialty(specialtyId);

  const setSpecialtyId = useCallback((id) => {
    setSpecialtyIdState(id);
    saveSpecialtyId(id);
    // Broadcast so listeners in other tabs/windows sync.
    window.dispatchEvent(new CustomEvent('lumina-specialty-change', { detail: { id } }));
  }, []);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'lumina_specialty' && e.newValue) setSpecialtyIdState(e.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <SpecialtyContext.Provider value={{ specialtyId, specialty, setSpecialtyId, specialties: SPECIALTIES }}>
      {children}
    </SpecialtyContext.Provider>
  );
};

export const useSpecialty = () => useContext(SpecialtyContext);
export default SpecialtyContext;
